import {
  ActionMenu,
  PreviewNote,
  RInputModal,
  RModal,
  TagPrivacy,
  TagTopic,
  TopicStates,
  UserAvatar,
} from '@/components'
import { Box, Button, IconButton, Link, Typography, useMediaQuery, useTheme } from '@mui/material'
import colors from '@/styles/colors'
import { Topic } from '@/types'
import { PrivacyType, PrivacyTypes, ResourceType, TopicType, TopicTypes } from '@/utils/constants'
import {
  ArrowBackIos,
  ChevronLeft,
  CommentOutlined,
  DeleteOutline,
  EditOutlined,
  MoreVert,
} from '@mui/icons-material'
import { useEffect, useMemo, useState } from 'react'
import {
  useDeleteTopic,
  useGetPermissions,
  useUpdatePatternTopic,
  useUpdateVisibility,
} from '@/hooks/api/dashboard'
import { toast } from 'react-hot-toast'
import { useNavigate } from 'react-router-dom'
import { useBrowserQuery, useLocalStorage } from '@/hooks/common'
import useAppStore from '@/store/useAppStore'
import ReactTimeago from 'react-timeago'
import { convertToGMT, getPlainNoteTextFromNote } from '@/utils/helper'
import { RBox } from '@/pages/__styled'
import SVG from 'react-inlinesvg'
import { SVGS } from '@/assets/svgs'
import { RoundedFilledButton } from '@/components/StyledComponents'
import CustomDivider from '@/components/CustomDivider'

interface HeaderProps {
  topic: Topic
  onClickViewInteractions: () => void
  onClickBack: () => void
  onClickComment: () => void
  onClickPrivacy: () => void
  onDeletedTopic?: () => void
}

export default function TopicDetailsHeader({
  topic: incomingTopic,
  onClickViewInteractions,
  onClickBack,
  onClickComment,
  onClickPrivacy,
  onDeletedTopic,
}: HeaderProps) {
  const [modalOpened, setModalOpened] = useState(false)
  const updateTopic = useUpdatePatternTopic()
  const updateVisibility = useUpdateVisibility()
  const getPermissions = useGetPermissions()
  const theme = useTheme()
  const [renameModalOpened, setRenameModalOpened] = useState(false)

  const lessThanSmall = useMediaQuery(theme.breakpoints.down('sm'))
  const topicData = TopicTypes[Number(incomingTopic.topicType)]
  const privacyData = PrivacyTypes[Number(incomingTopic.visibility)]

  const [ewUser] = useLocalStorage('ewUser', null)
  const deleteTopic = useDeleteTopic(incomingTopic.topicID)
  const navigate = useNavigate()
  const { removeTopic } = useAppStore()
  const query = useBrowserQuery()
  const topicID = query.get('topicID') || ''

  const [topic, setTopic] = useState(incomingTopic)
  const isMyTopic = ewUser?.userID == topic.userID

  useEffect(() => {
    setTopic(incomingTopic)
  }, [incomingTopic])

  useEffect(() => {
    if (deleteTopic.isSuccess) {
      removeTopic(topicID)
      onDeletedTopic && onDeletedTopic()
    }
  }, [deleteTopic.isSuccess])

  const onCloseModal = () => {
    setModalOpened(false)
  }

  const onDeleteTopic = () => {
    toast.promise(deleteTopic.mutateAsync(), {
      loading: 'Deleting...',
      success: <Typography variant='p'>Successfully deleted</Typography>,
      error: <Typography variant='p'>Failed to delete</Typography>,
    })
  }

  const topicActions = useMemo(() => {
    let items = [
      {
        icon: <EditOutlined />,
        name: 'Rename',
        onClick: () => setRenameModalOpened(true),
      },
    ]

    if (topic.visibility == PrivacyType.PRIVATE.toString()) {
      items.push({
        icon: <SVG src={SVGS.FilterPrivacyPublic} />,
        name: 'Save as Public',
        onClick: () => changeVisibility(PrivacyType.PUBLIC),
      })
    } else {
      items.push({
        icon: <SVG src={SVGS.FilterPrivacyPrivate} />,
        name: 'Make Private',
        onClick: () => changeVisibility(PrivacyType.PRIVATE),
      })
    }
    items.push({
      icon: <DeleteOutline />,
      name: 'Delete',
      onClick: () => setModalOpened(true),
    })

    return items
  }, [topic])

  const changeVisibility = (newPrivacy: PrivacyType) => {
    if (topic != null) {
      // Get Permission ID

      const updatePerm = (permID: string) => {
        updateVisibility
          .mutateAsync({
            resourceID: topic.topicID,
            cliqueTypes: topic.cliqueTypes,
            cliques: [],
            connections: [],
            accessTypeID: newPrivacy.toString(),
            permissionID: permID,
            resourceType: ResourceType.TOPIC,
          })
          .then((value) => {
            toast.remove('perm-load')
            toast.success('Updated')

            setTopic({
              ...topic,
              visibility: newPrivacy.toString(),
            })
          })
          .catch((err) => {
            toast.remove('perm-load')
            toast.error(err.toString())
          })
      }

      toast.loading('Updating...', { id: 'perm-load' })
      getPermissions
        .mutateAsync(topic.topicID)
        .then((permData) => {
          const { permissionID } = permData
          updatePerm(permissionID)
        })
        .catch((err) => {
          toast.remove('perm-load')
        })
    }
  }

  const onConfirmRenameTopic = (text: string) => {
    if (topic) {
      toast.loading('Updating...', { id: 'update-topic' })
      updateTopic
        .mutateAsync({ topicID: topic.topicID, topicName: text })
        .then((value) => {
          toast.remove('update-topic')
          toast.success('Updated Successfully.')

          const noteText = {
            text: `<p>${text}</p>`,
            attachments: [],
          }

          let updatedTopic = {
            ...topic,
            topicName: text,
            firstNote: {
              ...topic.firstNote,
              noteText: JSON.stringify(noteText),
            },
          }
          setTopic((prev) => ({ ...updatedTopic }))
        })
        .catch((e) => {
          toast.remove('update-topic')
          toast.error(e.toString())
        })
    }
  }

  const noteCount = useMemo(() => {
    return Number(topic.noteCount) - 1
  }, [topic])

  const totalInteractions = useMemo(() => {
    return Number(topic.totalLikes) + Number(topic.totalComments)
  }, [topic])

  if (!topicData || !privacyData) return null

  return (
    <Box>
      <Box
        display={'flex'}
        flexDirection={'row'}
        alignItems={'center'}
        justifyContent={'space-between'}
        sx={{
          padding: '24px 32px',
          borderBottom: '1px solid rgba(226, 232, 240, 0.8)',
          background: 'linear-gradient(180deg, #ffffff 0%, #fafafa 100%)',
        }}
      >
        <Box display={'flex'} flexDirection={'row'} gap={'20px'} alignItems={'center'}>
          <IconButton
            onClick={onClickBack}
            sx={{
              width: 44,
              height: 44,
              backgroundColor: 'rgba(248, 250, 252, 0.8)',
              border: '1px solid rgba(226, 232, 240, 0.6)',
              borderRadius: '12px',
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                backgroundColor: '#ffffff',
                transform: 'translateX(-2px)',
                borderColor: '#6366f1',
                boxShadow: '0 4px 12px rgba(99, 102, 241, 0.15)',
              },
              '&:active': {
                transform: 'translateX(0)',
              },
            }}
          >
            <SVG src={SVGS.BackArrow} color='#475569' width={20} height={20} />
          </IconButton>
          <Box display={'flex'} flexDirection={'column'} gap={'8px'}>
            <Typography
              fontSize={'22px'}
              fontWeight={'700'}
              color='#0f172a'
              sx={{
                lineHeight: 1.3,
                letterSpacing: '-0.02em',
              }}
            >{`${getPlainNoteTextFromNote(topic.firstNote)}`}</Typography>
            <RBox sx={{ alignItems: 'center', justifyContent: 'flex-start', gap: '12px' }}>
              {topic.dateAdded && (
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '4px 12px',
                    borderRadius: '8px',
                    backgroundColor: 'rgba(248, 250, 252, 0.8)',
                    border: '1px solid rgba(226, 232, 240, 0.6)',
                  }}
                >
                  <Typography
                    fontSize={'13px'}
                    fontWeight={500}
                    sx={{ color: '#64748b', letterSpacing: '-0.01em' }}
                    whiteSpace={'nowrap'}
                  >
                    {`Added `}
                    <ReactTimeago date={convertToGMT(topic.dateAdded)} />
                  </Typography>
                </Box>
              )}
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '4px 12px',
                  borderRadius: '8px',
                  backgroundColor: 'rgba(99, 102, 241, 0.08)',
                  border: '1px solid rgba(99, 102, 241, 0.15)',
                }}
              >
                <Typography
                  fontSize={'13px'}
                  fontWeight={600}
                  sx={{ color: '#6366f1', letterSpacing: '-0.01em' }}
                >{`${noteCount || 0} Photos`}</Typography>
              </Box>
            </RBox>
          </Box>
        </Box>
        <RBox gap={'12px'} justifyContent={'center'} alignItems={'center'}>
          {isMyTopic ? (
            <>
              {topic.visibility == PrivacyType.PRIVATE.toString() ? (
                <Button
                  variant='contained'
                  sx={{
                    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                    color: 'white',
                    padding: '10px 20px',
                    borderRadius: '12px',
                    textTransform: 'none',
                    fontSize: '14px',
                    fontWeight: 600,
                    letterSpacing: '-0.01em',
                    boxShadow: '0 4px 12px rgba(99, 102, 241, 0.25), 0 2px 4px rgba(0, 0, 0, 0.1)',
                    border: 'none',
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #5558e3 0%, #7c3aed 100%)',
                      transform: 'translateY(-2px)',
                      boxShadow:
                        '0 8px 20px rgba(99, 102, 241, 0.35), 0 4px 8px rgba(0, 0, 0, 0.15)',
                    },
                    '&:active': {
                      transform: 'translateY(0)',
                    },
                  }}
                  onClick={() => {
                    changeVisibility(PrivacyType.PUBLIC)
                  }}
                  startIcon={<SVG src={SVGS.FilterPrivacyPublic} width={16} height={16} />}
                >
                  {'Make Public'}
                </Button>
              ) : (
                <Button
                  variant='contained'
                  sx={{
                    background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
                    color: 'white',
                    padding: '10px 20px',
                    borderRadius: '12px',
                    textTransform: 'none',
                    fontSize: '14px',
                    fontWeight: 600,
                    letterSpacing: '-0.01em',
                    boxShadow: '0 4px 12px rgba(15, 23, 42, 0.25), 0 2px 4px rgba(0, 0, 0, 0.1)',
                    border: 'none',
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 20px rgba(15, 23, 42, 0.35), 0 4px 8px rgba(0, 0, 0, 0.15)',
                    },
                    '&:active': {
                      transform: 'translateY(0)',
                    },
                  }}
                  onClick={() => {
                    changeVisibility(PrivacyType.PRIVATE)
                  }}
                  startIcon={<SVG src={SVGS.FilterPrivacyPrivate} width={16} height={16} />}
                >
                  {'Make Private'}
                </Button>
              )}

              {topic.userID === ewUser?.userID && (
                <IconButton
                  sx={{
                    width: 44,
                    height: 44,
                    backgroundColor: 'rgba(248, 250, 252, 0.8)',
                    border: '1px solid rgba(226, 232, 240, 0.6)',
                    borderRadius: '12px',
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      backgroundColor: '#ffffff',
                      borderColor: '#cbd5e1',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                    },
                  }}
                >
                  <ActionMenu
                    id='topic-item-menu'
                    parent={<SVG src={SVGS.MoreIcon} color='#64748b' width={20} height={20} />}
                    actions={topicActions}
                  />
                </IconButton>
              )}
            </>
          ) : (
            <RBox gap={'16px'} alignItems={'center'}>
              <Box
                sx={{
                  position: 'relative',
                  display: 'inline-flex',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    inset: '-3px',
                    borderRadius: '50%',
                    padding: '2px',
                    background: 'linear-gradient(135deg, #6366f1 0%, #ec4899 100%)',
                    WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                    WebkitMaskComposite: 'xor',
                    maskComposite: 'exclude',
                  },
                }}
              >
                <UserAvatar
                  borderRadius={22}
                  name={topic.userName}
                  url={topic.profilePicThumbURL}
                  size={44}
                />
              </Box>
              <Box sx={{ alignItems: 'center' }}>
                <Link href={`/profile/user?id=${topic.userID}`} underline='none'>
                  <Typography
                    fontSize={'15px'}
                    fontWeight={600}
                    color='#0f172a'
                    sx={{
                      letterSpacing: '-0.01em',
                      transition: 'color 0.2s',
                      '&:hover': {
                        color: '#6366f1',
                      },
                    }}
                  >
                    {topic.userName}
                  </Typography>
                </Link>
              </Box>
            </RBox>
          )}
        </RBox>
      </Box>
      <RBox
        padding={'20px 32px'}
        justifyContent={'space-between'}
        alignItems={'center'}
        sx={{
          borderBottom: '1px solid rgba(226, 232, 240, 0.8)',
          backgroundColor: '#fafafa',
        }}
      >
        <Box flex={1} display='flex' alignItems={'center'} gap={'16px'}>
          <TopicStates topic={topic} />
        </Box>
        {totalInteractions > 0 && (
          <Button
            onClick={onClickViewInteractions}
            sx={{
              padding: '8px 16px',
              borderRadius: '10px',
              textTransform: 'none',
              backgroundColor: 'rgba(99, 102, 241, 0.08)',
              border: '1px solid rgba(99, 102, 241, 0.15)',
              fontSize: '14px',
              fontWeight: 600,
              color: '#6366f1',
              letterSpacing: '-0.01em',
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                backgroundColor: 'rgba(99, 102, 241, 0.12)',
                borderColor: 'rgba(99, 102, 241, 0.25)',
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 12px rgba(99, 102, 241, 0.15)',
              },
              '&:active': {
                transform: 'translateY(0)',
              },
            }}
          >
            {`${totalInteractions} Interactions`}
          </Button>
        )}
      </RBox>

      <RModal
        open={modalOpened}
        onClose={onCloseModal}
        title='Delete Topic'
        description='Are you sure you want to delete this topic?'
        submitText='Delete'
        onSubmit={onDeleteTopic}
      />
      <RInputModal
        open={renameModalOpened}
        maxLength={30}
        onClose={() => setRenameModalOpened(false)}
        title='Change Topic Name'
        text={topic.topicName}
        placeholder={'Enter Topic Description'}
        description=''
        submitText='Save'
        onSubmit={onConfirmRenameTopic}
      />
    </Box>
  )
}
